import * as React from 'react'
import {
    getCachedUiLocaleCatalog,
    getCachedUiLocaleSourceCatalog,
    loadUiLocaleCatalog,
    loadUiLocaleSourceCatalog,
    normalizeUiLocaleLanguage,
    normalizeUiLocaleLookupText
} from '../utils/uiLocaleCatalog'

const TRANSLATABLE_ATTRIBUTES = Object.freeze(['placeholder', 'title', 'aria-label', 'alt'])
const TRANSLATION_BATCH_SIZE = 300
const textNodeOriginals = new WeakMap()
const textNodeAppliedValues = new WeakMap()
const elementAttributeOriginals = new WeakMap()
const elementAttributeAppliedValues = new WeakMap()
const machineDomTranslationCache = new Map()
const pendingMachineDomTranslationKeys = new Set()

const preserveEdgeWhitespace = (originalValue, translatedValue) => {
    const originalText = String(originalValue ?? '')
    const translatedText = String(translatedValue ?? '').trim()
    const leadingWhitespace = originalText.match(/^\s*/)?.[0] ?? ''
    const trailingWhitespace = originalText.match(/\s*$/)?.[0] ?? ''

    return `${leadingWhitespace}${translatedText}${trailingWhitespace}`
}

const getOriginalElementAttributeValue = (element, attributeName) => {
    let originalAttributes = elementAttributeOriginals.get(element)
    if (!originalAttributes) {
        originalAttributes = new Map()
        elementAttributeOriginals.set(element, originalAttributes)
    }

    if (!originalAttributes.has(attributeName))
        originalAttributes.set(attributeName, String(element.getAttribute(attributeName) ?? ''))

    return originalAttributes.get(attributeName) ?? ''
}

const setOriginalElementAttributeValue = (element, attributeName, value) => {
    let originalAttributes = elementAttributeOriginals.get(element)
    if (!originalAttributes) {
        originalAttributes = new Map()
        elementAttributeOriginals.set(element, originalAttributes)
    }

    originalAttributes.set(attributeName, String(value ?? ''))
}

const getAppliedElementAttributeValue = (element, attributeName) => {
    const appliedAttributes = elementAttributeAppliedValues.get(element)
    return String(appliedAttributes?.get(attributeName) ?? '')
}

const setAppliedElementAttributeValue = (element, attributeName, value) => {
    let appliedAttributes = elementAttributeAppliedValues.get(element)
    if (!appliedAttributes) {
        appliedAttributes = new Map()
        elementAttributeAppliedValues.set(element, appliedAttributes)
    }

    appliedAttributes.set(attributeName, String(value ?? ''))
}

const shouldSkipTextNode = textNode => {
    const parentElement = textNode.parentElement
    if (!parentElement)
        return true

    if (parentElement.closest('[data-no-ui-translate="true"]'))
        return true

    if (parentElement.closest('script, style, textarea, code, pre'))
        return true

    return false
}

const lowerCaseFirstCharacter = value => {
    const source = String(value ?? '')
    if (!source)
        return source

    return source.charAt(0).toLocaleLowerCase() + source.slice(1)
}

const readCatalogFragmentTranslation = (catalog, value) => {
    const normalizedValue = normalizeUiLocaleLookupText(value)
    if (!normalizedValue)
        return ''

    const directMatch = String(catalog?.[normalizedValue] ?? '').trim()
    if (directMatch)
        return directMatch

    const capitalizedValue = normalizedValue.charAt(0).toUpperCase() + normalizedValue.slice(1)
    const capitalizedMatch = String(catalog?.[capitalizedValue] ?? '').trim()
    if (capitalizedMatch)
        return /^[a-z]/.test(normalizedValue) ? lowerCaseFirstCharacter(capitalizedMatch) : capitalizedMatch

    const lowerCaseValue = normalizedValue.toLowerCase()
    const lowerCaseMatch = String(catalog?.[lowerCaseValue] ?? '').trim()
    if (lowerCaseMatch)
        return lowerCaseMatch

    return ''
}

const translateCompositeUiText = (value, catalog) => {
    const normalizedValue = normalizeUiLocaleLookupText(value)
    if (!normalizedValue)
        return ''

    const exactMatch = readCatalogFragmentTranslation(catalog, normalizedValue)
    if (exactMatch)
        return exactMatch

    const colonMatch = normalizedValue.match(/^([^:]{1,120}?):\s+(.+)$/)
    if (colonMatch) {
        const translatedLeft = readCatalogFragmentTranslation(catalog, colonMatch[1])
        const translatedRight = readCatalogFragmentTranslation(catalog, colonMatch[2])
        if (translatedLeft || translatedRight)
            return `${translatedLeft || colonMatch[1]}: ${translatedRight || colonMatch[2]}`
    }

    const savedByMatch = normalizedValue.match(/^(Saved by)\s+(.+)$/i)
    if (savedByMatch) {
        const translatedLabel = readCatalogFragmentTranslation(catalog, savedByMatch[1])
        if (translatedLabel)
            return `${translatedLabel} ${savedByMatch[2]}`
    }

    const countLabelMatch = normalizedValue.match(/^(\d+)\s+([A-Za-z][A-Za-z ]+)$/)
    if (countLabelMatch) {
        const translatedLabel = readCatalogFragmentTranslation(catalog, countLabelMatch[2])
        if (translatedLabel)
            return `${countLabelMatch[1]} ${translatedLabel}`
    }

    const trailingWordMatch = normalizedValue.match(/^(.+)\s+([A-Za-z][A-Za-z ]+)$/)
    if (trailingWordMatch) {
        const translatedTail = readCatalogFragmentTranslation(catalog, trailingWordMatch[2])
        if (translatedTail)
            return `${trailingWordMatch[1]} ${translatedTail}`
    }

    const parentheticalCountMatch = normalizedValue.match(/^(.+?)\s+\((\d+)(?:\s+([A-Za-z][A-Za-z ]+))?\)$/)
    if (parentheticalCountMatch) {
        const translatedLabel = readCatalogFragmentTranslation(catalog, parentheticalCountMatch[1])
        const translatedSuffix = readCatalogFragmentTranslation(catalog, parentheticalCountMatch[3] ?? '')
        if (translatedLabel || translatedSuffix)
            return `${translatedLabel || parentheticalCountMatch[1]} (${parentheticalCountMatch[2]}${translatedSuffix ? ` ${translatedSuffix}` : parentheticalCountMatch[3] ? ` ${parentheticalCountMatch[3]}` : ''})`
    }

    return ''
}

const buildMachineDomTranslationCacheKey = (language, text) =>
    `${language}::${normalizeUiLocaleLookupText(text)}`

const readMachineDomTranslation = (language, text) => {
    const cacheKey = buildMachineDomTranslationCacheKey(language, text)
    return machineDomTranslationCache.has(cacheKey) ?
        String(machineDomTranslationCache.get(cacheKey) ?? '') :
        ''
}

const hasSourceCatalogMatch = (sourceCatalog, value) =>
    Boolean(translateCompositeUiText(value, sourceCatalog))

const isLikelyMachineTranslatableUiText = value => {
    const text = normalizeUiLocaleLookupText(value)
    if (text.length < 2 || text.length > 220)
        return false

    if (!/[A-Za-z]/.test(text) || !/\s/.test(text))
        return false

    if (/[@{}[\]<>]|https?:\/\/|www\./i.test(text))
        return false

    if (/^(rgba?|hsla?|linear-gradient|calc|url)\(/i.test(text))
        return false

    if (/^(const|let|return|function|import|export)\b/.test(text))
        return false

    if (/[=|]|\b(?:true|false|null|undefined)\b/.test(text))
        return false

    return true
}

const queueMachineDomTranslation = (pendingTexts, language, sourceText, sourceCatalog) => {
    if (!pendingTexts || language === 'en')
        return

    const normalizedText = normalizeUiLocaleLookupText(sourceText)
    if (!normalizedText)
        return

    const cacheKey = buildMachineDomTranslationCacheKey(language, normalizedText)
    if (machineDomTranslationCache.has(cacheKey) || pendingMachineDomTranslationKeys.has(cacheKey))
        return

    if (!hasSourceCatalogMatch(sourceCatalog, normalizedText) && !isLikelyMachineTranslatableUiText(normalizedText))
        return

    pendingTexts.add(normalizedText)
}

const resolveTranslatedUiText = (sourceText, catalog, language, sourceCatalog, pendingTexts) => {
    const catalogTranslation = translateCompositeUiText(sourceText, catalog)
    if (catalogTranslation)
        return catalogTranslation

    const machineTranslation = readMachineDomTranslation(language, sourceText)
    if (machineTranslation)
        return machineTranslation

    queueMachineDomTranslation(pendingTexts, language, sourceText, sourceCatalog)
    return ''
}

const applyCatalogTextTranslation = (textNode, catalog, language, sourceCatalog, pendingTexts) => {
    if (!textNode?.isConnected || shouldSkipTextNode(textNode))
        return

    const currentText = String(textNode.textContent ?? '')
    const storedOriginalText = textNodeOriginals.has(textNode) ?
        textNodeOriginals.get(textNode) :
        currentText
    const appliedText = String(textNodeAppliedValues.get(textNode) ?? '')

    if (!textNodeOriginals.has(textNode))
        textNodeOriginals.set(textNode, currentText)

    if (currentText !== storedOriginalText && currentText !== appliedText)
        textNodeOriginals.set(textNode, currentText)

    const originalText = String(textNodeOriginals.get(textNode) ?? currentText)

    const nextLookupKey = normalizeUiLocaleLookupText(originalText)
    if (!nextLookupKey) {
        textNodeAppliedValues.set(textNode, originalText)
        textNode.textContent = originalText
        return
    }

    if (language === 'en') {
        textNodeAppliedValues.set(textNode, originalText)
        if (currentText !== originalText)
            textNode.textContent = originalText
        return
    }

    const translatedText = resolveTranslatedUiText(nextLookupKey, catalog, language, sourceCatalog, pendingTexts)
    const nextTextContent = translatedText ?
        preserveEdgeWhitespace(originalText, translatedText) :
        originalText

    textNodeAppliedValues.set(textNode, nextTextContent)
    if (currentText !== nextTextContent)
        textNode.textContent = nextTextContent
}

const applyCatalogAttributeTranslation = (element, attributeName, catalog, language, sourceCatalog, pendingTexts) => {
    if (!(element instanceof Element))
        return

    if (!element.hasAttribute(attributeName))
        return

    if (element.closest('[data-no-ui-translate="true"]'))
        return

    const currentValue = String(element.getAttribute(attributeName) ?? '')
    const originalValue = getOriginalElementAttributeValue(element, attributeName)
    const appliedValue = getAppliedElementAttributeValue(element, attributeName)

    if (currentValue !== originalValue && currentValue !== appliedValue)
        setOriginalElementAttributeValue(element, attributeName, currentValue)

    const sourceValue = getOriginalElementAttributeValue(element, attributeName)
    const nextLookupKey = normalizeUiLocaleLookupText(sourceValue)

    if (!nextLookupKey) {
        setAppliedElementAttributeValue(element, attributeName, sourceValue)
        element.setAttribute(attributeName, sourceValue)
        return
    }

    if (language === 'en') {
        setAppliedElementAttributeValue(element, attributeName, sourceValue)
        if (currentValue !== sourceValue)
            element.setAttribute(attributeName, sourceValue)
        return
    }

    const translatedValue = resolveTranslatedUiText(nextLookupKey, catalog, language, sourceCatalog, pendingTexts)
    const nextAttributeValue = translatedValue ?
        preserveEdgeWhitespace(sourceValue, translatedValue) :
        sourceValue

    setAppliedElementAttributeValue(element, attributeName, nextAttributeValue)
    if (currentValue !== nextAttributeValue)
        element.setAttribute(attributeName, nextAttributeValue)
}

const applyCatalogToDomTree = (rootNode, catalog, language, options = {}) => {
    const sourceCatalog = options.sourceCatalog ?? {}
    const pendingTexts = options.pendingTexts

    if (!(rootNode instanceof Node))
        return

    if (rootNode.nodeType === Node.TEXT_NODE) {
        applyCatalogTextTranslation(rootNode, catalog, language, sourceCatalog, pendingTexts)
        return
    }

    if (!(rootNode instanceof Element) && !(rootNode instanceof DocumentFragment))
        return

    if (rootNode instanceof Element)
        TRANSLATABLE_ATTRIBUTES.forEach(attributeName =>
            applyCatalogAttributeTranslation(rootNode, attributeName, catalog, language, sourceCatalog, pendingTexts))

    const walker = document.createTreeWalker(rootNode, NodeFilter.SHOW_TEXT)
    let currentNode = walker.nextNode()

    while (currentNode) {
        applyCatalogTextTranslation(currentNode, catalog, language, sourceCatalog, pendingTexts)
        currentNode = walker.nextNode()
    }

    if (rootNode instanceof Element) {
        rootNode.querySelectorAll('*').forEach(element =>
            TRANSLATABLE_ATTRIBUTES.forEach(attributeName =>
                applyCatalogAttributeTranslation(element, attributeName, catalog, language, sourceCatalog, pendingTexts)))
    }
}

const getDomTranslationRoot = () =>
    document.body ?? document.getElementById('root')

export default function useExactUiLocaleDomTranslation(language) {
    const normalizedLanguage = normalizeUiLocaleLanguage(language)

    React.useEffect(() => {
        if (normalizedLanguage === 'en') {
            const rootElement = getDomTranslationRoot()
            if (rootElement)
                applyCatalogToDomTree(rootElement, {}, normalizedLanguage, {
                    sourceCatalog: {},
                    pendingTexts: undefined
                })

            return undefined
        }

        let observer
        let active = true
        const controller = new AbortController()

        const requestMachineTranslations = async texts => {
            const requestTexts = [...new Set(
                (Array.isArray(texts) ? texts : [])
                    .map(text => normalizeUiLocaleLookupText(text))
                    .filter(Boolean)
            )].filter(text => {
                const cacheKey = buildMachineDomTranslationCacheKey(normalizedLanguage, text)
                return !machineDomTranslationCache.has(cacheKey) &&
                    !pendingMachineDomTranslationKeys.has(cacheKey)
            })

            if (requestTexts.length === 0)
                return

            requestTexts.forEach(text =>
                pendingMachineDomTranslationKeys.add(buildMachineDomTranslationCacheKey(normalizedLanguage, text)))

            try {
                for (let index = 0; index < requestTexts.length; index += TRANSLATION_BATCH_SIZE) {
                    const batch = requestTexts.slice(index, index + TRANSLATION_BATCH_SIZE)
                    const response = await fetch('/translate-texts', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            targetLanguage: normalizedLanguage,
                            texts: batch
                        }),
                        signal: controller.signal
                    })

                    if (!response.ok)
                        throw new Error(`DOM translation request failed (${response.status})`)

                    const payload = await response.json()
                    const translatedMap = payload?.translations && typeof payload.translations === 'object' ?
                        payload.translations :
                        {}

                    batch.forEach(sourceText => {
                        machineDomTranslationCache.set(
                            buildMachineDomTranslationCacheKey(normalizedLanguage, sourceText),
                            String(translatedMap[sourceText] ?? sourceText)
                        )
                    })
                }

                if (active)
                    applyCurrentDomTranslations()
            }
            catch (error) {
                if (error?.name !== 'AbortError')
                    console.warn('[UiDomTranslation] Could not fetch DOM translations:', error)
            }
            finally {
                requestTexts.forEach(text =>
                    pendingMachineDomTranslationKeys.delete(buildMachineDomTranslationCacheKey(normalizedLanguage, text)))
            }
        }

        const applyCurrentDomTranslations = rootNode => {
            const catalog = getCachedUiLocaleCatalog(normalizedLanguage)
            const sourceCatalog = getCachedUiLocaleSourceCatalog()
            const rootElement = rootNode ?? getDomTranslationRoot()
            if (!rootElement)
                return

            const pendingTexts = new Set()
            applyCatalogToDomTree(rootElement, catalog, normalizedLanguage, {
                sourceCatalog,
                pendingTexts
            })

            void requestMachineTranslations([...pendingTexts])
        }

        const syncDomTranslations = async () => {
            if (normalizedLanguage !== 'en')
                await Promise.all([
                    loadUiLocaleCatalog(normalizedLanguage),
                    loadUiLocaleSourceCatalog()
                ])

            if (!active)
                return

            const rootElement = getDomTranslationRoot()
            if (!rootElement)
                return

            applyCurrentDomTranslations(rootElement)

            observer = new MutationObserver(mutations => {
                const nextCatalog = getCachedUiLocaleCatalog(normalizedLanguage)
                const sourceCatalog = getCachedUiLocaleSourceCatalog()
                const pendingTexts = new Set()

                mutations.forEach(mutation => {
                    if (mutation.type === 'characterData')
                        applyCatalogToDomTree(mutation.target, nextCatalog, normalizedLanguage, {
                            sourceCatalog,
                            pendingTexts
                        })

                    if (mutation.type === 'attributes' && mutation.target instanceof Element)
                        applyCatalogAttributeTranslation(
                            mutation.target,
                            mutation.attributeName,
                            nextCatalog,
                            normalizedLanguage,
                            sourceCatalog,
                            pendingTexts
                        )

                    mutation.addedNodes.forEach(node =>
                        applyCatalogToDomTree(node, nextCatalog, normalizedLanguage, {
                            sourceCatalog,
                            pendingTexts
                        }))
                })

                void requestMachineTranslations([...pendingTexts])
            })

            observer.observe(rootElement, {
                subtree: true,
                childList: true,
                characterData: true,
                attributes: true,
                attributeFilter: [...TRANSLATABLE_ATTRIBUTES]
            })
        }

        syncDomTranslations()

        return () => {
            active = false
            controller.abort()
            observer?.disconnect()
        }
    }, [normalizedLanguage])
}
